import { IconButton, Stack, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { DiscordIcon } from '@noah-labs/fe-shared-ui-assets/muiSvgIcons/DiscordIcon';
import { InstagramIcon } from '@noah-labs/fe-shared-ui-assets/muiSvgIcons/InstagramIcon';
import { LinkedinIcon } from '@noah-labs/fe-shared-ui-assets/muiSvgIcons/LinkedinIcon';
import { PlaceholderIcon } from '@noah-labs/fe-shared-ui-assets/muiSvgIcons/PlaceholderIcon';
import { TelegramIcon } from '@noah-labs/fe-shared-ui-assets/muiSvgIcons/TelegramIcon';
import { TwitterIcon } from '@noah-labs/fe-shared-ui-assets/muiSvgIcons/TwitterIcon';
import type { TpMuiSvgIcon } from '@noah-labs/fe-shared-ui-assets/types/muiSvgIcon';
import type {
  MarkdownRemarkFrontmatterSocialMenuSocialLinks,
  Maybe,
} from '../../../generated/types';

export type PpSocialMenu = {
  items?: Maybe<MarkdownRemarkFrontmatterSocialMenuSocialLinks>[];
};

type TpSocialItemName = 'Discord' | 'Instagram' | 'LinkedIn' | 'Telegram' | 'Twitter';

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.websiteBrand.greyscale.plainBlack,
  ':hover': {
    backgroundColor: theme.palette.websiteBrand.greyscale.plainBlack,
  },
})) as typeof IconButton;

export function SocialMenu({ items }: PpSocialMenu): React.ReactElement | null {
  const theme = useTheme();
  if (!items) {
    return null;
  }

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={2}
      sx={{ paddingY: 2 }}
      width="fit-content"
    >
      {items.map((item) => {
        if (!item?.socialName || !item?.socialURL) {
          return null;
        }
        const socialItemName = item.socialName as TpSocialItemName;
        let Icon: TpMuiSvgIcon;
        switch (socialItemName) {
          case 'Discord': {
            Icon = DiscordIcon;
            break;
          }
          case 'Instagram': {
            Icon = InstagramIcon;
            break;
          }
          case 'LinkedIn': {
            Icon = LinkedinIcon;
            break;
          }
          case 'Telegram': {
            Icon = TelegramIcon;
            break;
          }
          case 'Twitter': {
            Icon = TwitterIcon;
            break;
          }
          // Use an empty icon so it acts as a placeholder
          default:
            Icon = PlaceholderIcon;
            break;
        }

        const applyCenteredIconOverride = socialItemName === 'Telegram';
        const centeredIconOverride = {
          left: '-1px',
          position: 'relative',
          top: '1px',
        };

        return (
          <StyledIconButton
            key={item.socialName}
            component="a"
            href={item.socialURL}
            rel="noreferrer"
            target="_blank"
          >
            <Icon
              fontSize="small"
              htmlColor={theme.palette.websiteBrand.greyscale.white}
              sx={{ ...(applyCenteredIconOverride && centeredIconOverride) }}
            />
            <Typography sx={visuallyHidden}>{item.socialName}</Typography>
          </StyledIconButton>
        );
      })}
    </Stack>
  );
}
